import clsx from 'clsx'
import FormField from 'components/FormField'
import { useStore } from 'context/store'
import { observer } from 'mobx-react'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ProjectTag } from 'Models'

interface ModalTagProps {
  visible: boolean
  onHide: () => void
  tag?: ProjectTag | null
}

interface FormValues {
  name: string
}

const defaultValues: FormValues = {
  name: '',
}

const ModalTag = ({ visible, onHide, tag }: ModalTagProps) => {
  const { t } = useTranslation()
  const {
    tagManagementStore: { create, update, loadingSubmit },
  } = useStore()

  const isEdit = !!tag?._id

  const { control, handleSubmit, reset, setError } = useForm<FormValues>({
    defaultValues,
  })

  useEffect(() => {
    if (visible) {
      if (tag) {
        reset({ name: tag.name || '' })
      } else {
        reset(defaultValues)
      }
    }
  }, [visible, tag, reset])

  const onSubmit = async (data: FormValues) => {
    try {
      if (isEdit && tag?._id) {
        await update(tag._id, { name: data.name.trim() })
      } else {
        await create({ name: data.name.trim() })
      }
      onHide()
    } catch (err: any) {
      if (err?.response?.data?.message) {
        setError('name', {
          type: 'manual',
          message: t(err.response.data.message),
        })
      }
    }
  }

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={isEdit ? t('Edit tag') : t('Create tag')}
      style={{ width: '450px' }}
      contentClassName="flex flex-column gap-4 overflow-auto"
    >
      <form
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
        className="p-fluid"
      >
        <FormField
          label={t('Tag name (1–30 chars)')}
          name="name"
          control={control}
          rules={{
            required: t('required', {
              field: t('Tag name (1–30 chars)'),
            }),
            maxLength: {
              value: 30,
              message: t('Please input < 30 charators'),
            },
            validate: (value) => {
              if (!value || !value.trim()) {
                return t('required', {
                  field: t('Tag name (1–30 chars)'),
                }) as string
              }
              if (value.trim().length > 30) {
                return t('Please input < 30 charators') as string
              }
              return true
            },
          }}
          render={({ field, fieldState }) => (
            <InputText
              {...field}
              placeholder={t('e.g. VIP, Volunteer')}
              maxLength={30}
              className={clsx({
                'p-invalid': fieldState.invalid,
              })}
            />
          )}
        />
        <div className="flex w-full gap-3 justify-content-end mt-4">
          <Button
            label={t('Save')}
            type="submit"
            className="w-fit"
            loading={loadingSubmit}
          />
          <Button
            label={t('Cancel')}
            severity="secondary"
            type="button"
            className="w-fit"
            onClick={onHide}
          />
        </div>
      </form>
    </Dialog>
  )
}

export default observer(ModalTag)
